import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";

import Header from "./header";
import ServingInfo from "./serving-info";
import CalorieInfo from "./calorie-info";
import FactItemList from "./fact-item-list";
import Footnotes from "./footnotes";
import Ingredients from "./ingredients";
import AllergyInfo from "./allergy-info";
import HealthClaims from "./health-claims";

import { footnoteTemplates, noteMarkList } from "../lib/constants";
import { getPicklist } from "../lib/data";

const Panel = ({ isNewTemplateUsed, isNutrition, onResetDefaultNutritionValues, onResetDefaultVitaminsValues, ...props }) => {
    const [allergenOptions, setAllergenOptions] = useState([]);
    const [healthClaimOptions, setHealthClaimOptions] = useState([]);

    useEffect(() => {
        async function fetchData() {
            const allergens = await getPicklist("Allergens");
            const healthClaims = await getPicklist("HealthClaims");

            if (!allergens || !healthClaims) {
                return;
            }

            setAllergenOptions(allergens);
            setHealthClaimOptions(healthClaims);
        }

        fetchData();
    }, []);

    const columnCount = props.panel.columns.length;
    const buttonsRemovePrepared =
        columnCount > 1
            ? Array(columnCount)
                  .fill()
                  .map((a, index) => {
                      return (
                          <button
                              key={`asPrepared_${index}of${columnCount}`}
                              className="button -small -right"
                              onClick={() => {
                                  props.removeColumn(index);
                              }}
                          >
                              {index + 1}
                          </button>
                      );
                  })
            : null;

    const servingInfoData = props.panel.columns.map((c) => {
        return {
            header: c.header,
            servingSize: c.servingSize,
            servingSizeMetric: c.servingSizeMetric,
            servingSizeVisual: c.servingSizeVisual,
            servingsPerContainer: c.servingsPerContainer,
            servingAccuracy: c.servingAccuracy,
            servingSizeDescription: c.servingSizeDescription,
        };
    });

    const calorieInfoData = props.panel.columns.reduce((col, c) => {
        col.push(c.calorie);

        return col;
    }, []);

    const dietaryInfoData = props.panel.columns.reduce((col, c) => {
        col.push({ facts: c.dietary.facts });

        return col;
    }, []);

    const vitamineralData = props.panel.columns.reduce((col, c) => {
        col.push({ facts: c.vitamineral.facts });

        return col;
    }, []);

    return (
        <div className="nutrition-panel panel">
            <div className="button-group">
                <h2>
                    {props.name}{" "}
                    <button
                        className="button -text -small"
                        onClick={props.removePanel}
                    >
                        Delete Panel
                    </button>
                </h2>
                {buttonsRemovePrepared}
                <button
                    className="button -small -right"
                    onClick={props.addColumn}
                >
                    <FontAwesomeIcon icon={faPlus} /> Prepared
                </button>
            </div>

            <Header
                value={props.panel.header}
                updateField={props.updateField}
            />

            <ServingInfo
                data={servingInfoData}
                panel={props.selectedIndex}
                updateField={props.updateField}
            />

            <CalorieInfo
                data={calorieInfoData}
                panel={props.selectedIndex}
                updateField={props.updateField}
                section="calorie"
            />

            <FactItemList
                enableWarnings
                data={dietaryInfoData}
                panel={props.selectedIndex}
                columnCount={columnCount}
                addFact={props.addFact}
                updateFacts={props.updateFacts}
                noteMarkList={noteMarkList}
                isNutrition={isNutrition}
                section="dietary"
                isNewTemplateUsed={isNewTemplateUsed}
                onResetDefaultNutritionValues={onResetDefaultNutritionValues}
            />

            <FactItemList
                data={vitamineralData}
                panel={props.selectedIndex}
                columnCount={columnCount}
                addFact={props.addFact}
                updateFacts={props.updateFacts}
                showDescription={props.showDescription}
                noteMarkList={noteMarkList}
                isNutrition={isNutrition}
                section="vitamineral"
                isNewTemplateUsed={isNewTemplateUsed}
                onResetDefaultVitaminsValues={onResetDefaultVitaminsValues}
            />

            <Footnotes
                data={props.panel.footnotes}
                presets={footnoteTemplates}
                noteMarkList={noteMarkList}
                updateField={props.updateField}
            />
            <Ingredients
                data={props.panel.ingredients}
                updateField={props.updateFieldDeep}
            />
            {props.panel.healthClaims && (
                <HealthClaims
                    data={props.panel.healthClaims}
                    allergenOptions={allergenOptions}
                    healthClaimOptions={healthClaimOptions}
                    allergens={props.panel.allergens}
                    updateField={props.updateField}
                />
            )}
            {props.panel.allergens && (
                <AllergyInfo
                    data={props.panel.allergens}
                    allergenOptions={allergenOptions}
                    updateField={props.updateField}
                />
            )}
        </div>
    );
};

export default Panel;
